import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Pencil } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { useNotice } from "../shared/useNotice";
import { ConnectorTypeRow, LocationRow, ocpiApi, OcpiSettings as Settings } from "../../../api/maintenance/ocpi";
import { canEditOcpi, errText, fmtTs, Modal } from "./ocpiShared";
import OcpiSubnav from "./OcpiSubnav";
import "../shared/maintenance-analytics.scss";
import "../stations/maintenance-stations.scss";
import "./ocpi.scss";

const STANDARDS = ["IEC_62196_T2", "IEC_62196_T2_COMBO", "GBT_AC", "GBT_DC", "CHADEMO", "IEC_60309_2_single_16",
  "IEC_60309_2_three_16", "IEC_60309_2_three_32", "DOMESTIC_D", "DOMESTIC_M", "IEC_62196_T1", "IEC_62196_T1_COMBO", "CHAOJI"];
const POWER = ["AC_1_PHASE", "AC_2_PHASE", "AC_2_PHASE_SPLIT", "AC_3_PHASE", "DC"];
const PARKING = ["", "ON_STREET", "PARKING_LOT", "PARKING_GARAGE", "UNDERGROUND_GARAGE", "ON_DRIVEWAY", "ALONG_MOTORWAY"];
const FACILITIES = ["HOTEL", "RESTAURANT", "CAFE", "MALL", "SUPERMARKET", "SPORT", "RECREATION_AREA", "NATURE", "MUSEUM",
  "BIKE_SHARING", "BUS_STOP", "TAXI_STAND", "TRAM_STOP", "METRO_STATION", "TRAIN_STATION", "AIRPORT", "PARKING_LOT",
  "CARPOOL_PARKING", "FUEL_STATION", "WIFI"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const OcpiSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [types, setTypes] = useState<ConnectorTypeRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [error, setError] = useNotice("error", "");
  const [, setSuccess] = useNotice("success", "");
  const [editLoc, setEditLoc] = useState<LocationRow | null>(null);
  const editable = canEditOcpi();

  const load = useCallback(async () => {
    try {
      const [s, t, l] = await Promise.all([ocpiApi.settings(), ocpiApi.connectorTypes(), ocpiApi.locations()]);
      setSettings(s.data);
      setTypes(t.data);
      setLocations(l.data);
    } catch (e: any) {
      setError(errText(e, "Failed to load OCPI settings"));
    }
  }, [setError]);

  useEffect(() => { load(); }, [load]);

  return (
    <MaintenanceLayout title="OCPI Roaming">
      <div className="ma">
        <OcpiSubnav />
        {error && <div className="ma-error">{error}</div>}
        {settings && <IdentityCard settings={settings} editable={editable} onSaved={(s) => { setSettings(s); setSuccess("Settings saved"); }} onError={setError} />}
        <div className="ma-card" style={{ marginTop: 16 }}>
          <div className="ma-card-h"><h3>Connector types</h3><span className="ma-hint">how each CMS socket type is described to partners</span></div>
          <div className="ma-tablewrap">
            <table className="ma-table">
              <thead><tr><th>Socket (CMS)</th><th className="num">Connectors</th><th>OCPI standard</th><th>Format</th><th>Power</th><th className="num">V</th><th className="num">A</th><th>Publish</th><th /></tr></thead>
              <tbody>{types.map((t) => <TypeRow key={t.socket_type_id} row={t} editable={editable} onSaved={() => { setSuccess("Mapping saved"); load(); }} onError={setError} />)}</tbody>
            </table>
          </div>
          <p className="ma-hint" style={{ padding: "0 16px 12px" }}>Rows marked ⚠ are best-effort guesses from the socket name — confirm them. Amperage is refined per connector from its kW rating.</p>
        </div>
        <div className="ma-card" style={{ marginTop: 16 }}>
          <div className="ma-card-h"><h3>Station publishing</h3><span className="ma-hint">only public, active stations are ever published</span></div>
          <div className="ma-tablewrap">
            <table className="ma-table">
              <thead><tr><th>Station</th><th>Type</th><th>Published</th><th>City (published)</th><th>PIN</th><th className="num">EVSEs</th><th>Last change</th><th /></tr></thead>
              <tbody>
                {locations.map((l) => (
                  <tr key={l.charging_station_id}>
                    <td>{l.station_name}<div className="sub">{(l.text_address || "").slice(0, 60)}</div></td>
                    <td>{l.station_type || "—"}</td>
                    <td>{l.is_published ? <span className="ma-pill ok">yes</span> : !l.publish ? <span className="ma-pill muted">hidden</span> : <span className="ma-pill muted">no</span>}</td>
                    <td>{l.published_city === "#NA" ? <span className="ma-pill warn">unknown — set it</span> : l.published_city || "—"}</td>
                    <td>{l.published_postal_code || "—"}</td>
                    <td className="num">{l.evse_count}</td>
                    <td>{fmtTs(l.last_updated)}</td>
                    <td>{editable && l.station_type === "public" && <button className="maint-btn-small" aria-label={`Edit ${l.station_name}`} onClick={() => setEditLoc(l)}><Pencil size={13} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {editLoc && <LocationForm row={editLoc} onClose={() => setEditLoc(null)} onSaved={() => { setEditLoc(null); setSuccess("Saved — published within ~15 s"); load(); }} />}
    </MaintenanceLayout>
  );
};

const IdentityCard: React.FC<{ settings: Settings; editable: boolean; onSaved: (s: Settings) => void; onError: (m: string) => void }> = ({
  settings, editable, onSaved, onError,
}) => {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof Settings, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const changed: Record<string, any> = {};
      (Object.keys(form) as (keyof Settings)[]).forEach((k) => {
        if (k !== "updated_at" && k !== "updated_by" && form[k] !== settings[k]) changed[k] = form[k] === "" ? null : form[k];
      });
      if (Object.keys(changed).length === 0) { setSaving(false); return; }
      onSaved((await ocpiApi.updateSettings(changed)).data);
    } catch (e: any) {
      onError(errText(e, "Failed to save settings"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="ma-card">
      <div className="ma-card-h"><h3>Our OCPI identity</h3><span className="ma-hint">updated {fmtTs(settings.updated_at)}{settings.updated_by ? ` by ${settings.updated_by}` : ""}</span></div>
      <div className="ma-card-b">
        {!settings.party_id && <div className="ma-error"><AlertTriangle size={14} /> Choose our 3-character party ID before onboarding partners. It cannot change once partners are connected.</div>}
        <div className="ocpi-form-grid">
          <label>Country code<input value={form.country_code} disabled={!editable} maxLength={2} onChange={(e) => set("country_code", e.target.value.toUpperCase())} /></label>
          <label>Party ID<input value={form.party_id || ""} disabled={!editable} maxLength={3} placeholder="e.g. CAD" onChange={(e) => set("party_id", e.target.value.toUpperCase())} /></label>
          <label>Business name<input value={form.business_name} disabled={!editable} maxLength={100} onChange={(e) => set("business_name", e.target.value)} /></label>
          <label>Website<input value={form.business_website || ""} disabled={!editable} onChange={(e) => set("business_website", e.target.value)} /></label>
          <label>Logo URL<input value={form.logo_url || ""} disabled={!editable} onChange={(e) => set("logo_url", e.target.value)} /></label>
          <label>Logo type (png/jpeg/svg)<input value={form.logo_type || ""} disabled={!editable} maxLength={4} onChange={(e) => set("logo_type", e.target.value)} /></label>
          <label>EVSE id prefix (eMI3)<input value={form.evse_id_prefix || ""} disabled={!editable} placeholder={`${form.country_code}*${form.party_id || "XXX"}`} onChange={(e) => set("evse_id_prefix", e.target.value.toUpperCase())} /></label>
          <label>Terms & conditions URL<input value={form.terms_and_conditions_url || ""} disabled={!editable} onChange={(e) => set("terms_and_conditions_url", e.target.value)} /></label>
          <label>Remote start for partners
            <select value={form.session_start_enabled ? "on" : "off"} disabled={!editable} onChange={(e) => set("session_start_enabled", e.target.value === "on")}>
              <option value="on">Enabled</option><option value="off">Disabled (kill switch)</option>
            </select>
          </label>
        </div>
        {editable && <button className="ma-btn primary" style={{ marginTop: 12 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save identity"}</button>}
      </div>
    </div>
  );
};

const TypeRow: React.FC<{ row: ConnectorTypeRow; editable: boolean; onSaved: () => void; onError: (m: string) => void }> = ({ row, editable, onSaved, onError }) => {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({
    standard: row.standard || "IEC_62196_T2", format: row.format || "SOCKET", power_type: row.power_type || "AC_1_PHASE",
    max_voltage: row.max_voltage || 230, max_amperage: row.max_amperage || 16, publish: row.mapped ? row.publish : false,
  });
  const save = async () => {
    try {
      await ocpiApi.updateConnectorType(row.socket_type_id, f);
      setEdit(false);
      onSaved();
    } catch (e: any) {
      onError(errText(e, "Failed to save mapping"));
    }
  };
  if (!edit) {
    return (
      <tr>
        <td>{row.needs_review && <span title="needs review">⚠ </span>}{row.socket_name}<div className="sub">{row.charging_current_mode}</div></td>
        <td className="num">{row.connectors}</td>
        <td>{row.standard || <span className="ma-pill warn">unmapped</span>}</td><td>{row.format || "—"}</td><td>{row.power_type || "—"}</td>
        <td className="num">{row.max_voltage ?? "—"}</td><td className="num">{row.max_amperage ?? "—"}</td>
        <td>{row.publish ? "yes" : "no"}</td>
        <td>{editable && <button className="maint-btn-small" aria-label={`Edit ${row.socket_name}`} onClick={() => setEdit(true)}><Pencil size={13} /></button>}</td>
      </tr>
    );
  }
  return (
    <tr>
      <td>{row.socket_name}</td><td className="num">{row.connectors}</td>
      <td className="ocpi-inline-edit"><select value={f.standard} onChange={(e) => setF({ ...f, standard: e.target.value })}>{STANDARDS.map((s) => <option key={s}>{s}</option>)}</select></td>
      <td className="ocpi-inline-edit"><select value={f.format} onChange={(e) => setF({ ...f, format: e.target.value as any })}><option>SOCKET</option><option>CABLE</option></select></td>
      <td className="ocpi-inline-edit"><select value={f.power_type} onChange={(e) => setF({ ...f, power_type: e.target.value })}>{POWER.map((s) => <option key={s}>{s}</option>)}</select></td>
      <td className="ocpi-inline-edit"><input type="number" value={f.max_voltage} onChange={(e) => setF({ ...f, max_voltage: Number(e.target.value) })} /></td>
      <td className="ocpi-inline-edit"><input type="number" value={f.max_amperage} onChange={(e) => setF({ ...f, max_amperage: Number(e.target.value) })} /></td>
      <td><input type="checkbox" checked={f.publish} onChange={(e) => setF({ ...f, publish: e.target.checked })} aria-label="Publish" /></td>
      <td style={{ display: "flex", gap: 6 }}><button className="maint-btn-small" onClick={save}>Save</button><button className="maint-btn-small" onClick={() => setEdit(false)}>Cancel</button></td>
    </tr>
  );
};

const LocationForm: React.FC<{ row: LocationRow; onClose: () => void; onSaved: () => void }> = ({ row, onClose, onSaved }) => {
  const [publish, setPublish] = useState(row.publish);
  const [name, setName] = useState(row.name || "");
  const [address, setAddress] = useState(row.address || "");
  const [city, setCity] = useState(row.city || "");
  const [postal, setPostal] = useState(row.postal_code || "");
  const [parking, setParking] = useState(row.parking_type || "");
  const [facilities, setFacilities] = useState<string[]>(row.facilities || []);
  const [allDay, setAllDay] = useState(row.opening_times ? row.opening_times.twentyfourseven : true);
  const [hours, setHours] = useState<{ weekday: number; period_begin: string; period_end: string }[]>(
    row.opening_times?.regular_hours?.length ? row.opening_times.regular_hours : [1, 2, 3, 4, 5, 6, 7].map((d) => ({ weekday: d, period_begin: "08:00", period_end: "20:00" })));
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      await ocpiApi.updateLocation(row.charging_station_id, {
        publish, name: name || null, address: address || null, city: city || null, postal_code: postal || null,
        parking_type: parking || null, facilities,
        opening_times: allDay ? null : { twentyfourseven: false, regular_hours: hours },
      });
      onSaved();
    } catch (e: any) {
      setErr(errText(e, "Failed to save"));
      setSaving(false);
    }
  };

  return (
    <Modal title={`Publishing — ${row.station_name}`} onClose={onClose} wide>
      <form onSubmit={submit}>
        <p className="ma-hint" style={{ marginTop: 0 }}>Blank fields use what the CMS has (address text is parsed for city and PIN).</p>
        <div className="ocpi-form-grid">
          <label>Published<select value={publish ? "yes" : "no"} onChange={(e) => setPublish(e.target.value === "yes")}><option value="yes">Yes</option><option value="no">No (hide from all partners)</option></select></label>
          <label>Display name<input value={name} placeholder={row.station_name} onChange={(e) => setName(e.target.value)} maxLength={255} /></label>
          <label>Street address<input value={address} onChange={(e) => setAddress(e.target.value)} maxLength={255} /></label>
          <label>City<input value={city} placeholder={row.published_city || ""} onChange={(e) => setCity(e.target.value)} maxLength={45} /></label>
          <label>PIN code<input value={postal} placeholder={row.published_postal_code || ""} onChange={(e) => setPostal(e.target.value)} maxLength={10} /></label>
          <label>Parking type<select value={parking} onChange={(e) => setParking(e.target.value)}>{PARKING.map((p) => <option key={p} value={p}>{p || "—"}</option>)}</select></label>
        </div>
        <div className="ocpi-chips" style={{ margin: "12px 0" }} role="group" aria-label="Facilities">
          {FACILITIES.map((fac) => (
            <button type="button" key={fac} className={`ma-tg ${facilities.includes(fac) ? "on" : ""}`}
                    onClick={() => setFacilities((x) => x.includes(fac) ? x.filter((y) => y !== fac) : [...x, fac])}>{fac.replace(/_/g, " ").toLowerCase()}</button>
          ))}
        </div>
        <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> Open 24/7 (leave on unless access is restricted)
        </label>
        {!allDay && (
          <div className="ocpi-form-grid" style={{ marginTop: 8 }}>
            {hours.map((h, i) => (
              <label key={h.weekday}>{DAYS[h.weekday - 1]}
                <span style={{ display: "flex", gap: 4 }}>
                  <input type="time" value={h.period_begin} onChange={(e) => setHours((x) => x.map((y, j) => j === i ? { ...y, period_begin: e.target.value } : y))} />
                  <input type="time" value={h.period_end} onChange={(e) => setHours((x) => x.map((y, j) => j === i ? { ...y, period_end: e.target.value } : y))} />
                </span>
              </label>
            ))}
          </div>
        )}
        {err && <p className="maint-form-error">{err}</p>}
        <div className="maint-form-actions">
          <button type="button" className="maint-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="maint-btn-primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
};

export default OcpiSettings;
